import { SHIFT } from './constants'
import { MbscResource } from '@mobiscroll/react'
import { Address, Carer, ResidentDtos, User, UserRole } from '@lmc/ts-common'

export enum RotaInstanceStatus {
  DRAFT = 'draft',
  PENDING = 'pending',
  DUE = 'due',
  IN_PROGRESS = 'in progress',
  OVERDUE = 'overdue',
  DONE = 'done',
  CANCELLED = 'cancelled'
}

export type RotaEventInstance = {
  id: string
  homeId: string
  rotaId: string
  subjectId: string
  patternId: string
  siteShift: boolean
  instanceTypeId: string
  instanceType: string
  fundingTypeId: string
  flexiType: boolean
  numberSupply: number
  startTime: string
  endTime: string
  plannedDuration: number
  expectedDuration: string
  orphaned: boolean
  title: string
  addressId: string
  skills: any[]
  rotaTags: any[]
  timezone: string
  status: RotaInstanceStatus
  createdBy: {
    id: string
    name: string
  }
  updatedBy: {
    id: string
    name: string
  }
  teamMembers?: TeamMember[]
  clockIns: any[]
  createdAt: string
  updatedAt: string
  totalResidentMinutes: number
  fundingType: string
  resident: {
    id: string
    name: {
      first: string
      last: string
    }
  }
  address: any[]
  deletedServiceUser: string
}

export type RotaResidentEventInstance = Partial<RotaEventInstance> & {
  id?: string
  start: string
  end: string
  title?: string
  subjectId: string
  resource: string
  color: string
  deletedSU: boolean
}

export type RotaShiftEventInstance = {
  id: string
  type: typeof SHIFT
  shifts: RotaEventInstance[]
  totalShifts: number
  fullyAllocated: number
}

export type RotaUserEventInstance = Partial<RotaEventInstance> & {
  id?: string
  start: string
  end: string
  title?: string
  subjectId: string
  resource: string[]
  color: string
}

export interface UserResource extends MbscResource {
  user: User
}

export interface ResidentResource extends MbscResource {
  resident?: ResidentDtos
  isDeletedSU: boolean
}

export type RotaBuilderFiltersType = {
  teamMembers?: string[]
  status?: RotaInstanceStatus[]
  serviceUsers?: string[]
  warnings?: string[]
  allocations?: string[]
  roles?: UserRole[]
  overtime?: boolean
  absences?: string[]
  typeOfEvent?: string[]
  flexi?: string[]
  siteShift?: boolean
}

export type RotaBuilderQueryFiltersType = RotaBuilderFiltersType & {
  selectedDate: string
}

export enum START_OVERLAY_TYPE {
  NO_PATTERNS = 'NO_PATTERNS',
  NO_ROTA = 'NO_ROTA'
}

export type ClockInLocation = { lat: number; lng: number; accuracy: number }

export enum GeolocationStatus {
  OUTSIDE_GEOFENCE = 'outside geofence',
  UNAVAILABLE = 'location unavailable',
  ADMIN_OVERRIDE = 'manual clock by admin',
  WITHIN_GEOFENCE = 'within geofence'
}

export type ClockIn = {
  teamMemberId: string
  actualDuration?: number
  actualDurationFormatted?: number
  clockInTime?: string
  clockInTimeSystem?: string
  clockInNotes?: ''
  clockedIn?: boolean
  clockOutTime?: string
  clockOutTimeSystem?: string
  clockOutNotes?: ''
  clockedOut?: boolean
  clockInGeoLocation: ClockInLocation
  clockOutGeoLocation: ClockInLocation
  clockInGeolocationStatus?: GeolocationStatus
  clockOutGeolocationStatus?: GeolocationStatus
  status: string
}

export type ClockInTeamMember = {
  assignedMinutes: number
  role: UserRole
  teamMemberId: string
  carer: Carer
  warnings?: {
    clockOutLate?: number
    clockOutEarly?: number
    clockInLate?: number
    clockInEarly?: number
    clockInMismatch?: number
    clockInLocationMismatch?: boolean
    clockOutLocationMismatch?: boolean
  }
}

export type TeamMember = {
  teamMemberId: string
  role?: string
  travelInfo: TravelInfo
}

export type TravelInfo = {
  fromInstanceId: string
  mode: TravelMode
  distanceText?: string
  distanceMeters?: number
  systemDistanceMeters?: number
  distanceInMiles?: number
  travelTimeText?: string
  travelTimeMinutes?: number
  status: GoogleAPiStatus
  error?: string
  errorCode:
    | GoogleDistanceApiStatus
    | GoogleDistanceElementStatus
    | InternalDistanceError
    | null
  fromAddress?: Address
  updatedBy?: string
}

export type TravelModeInfo = {
  name: TravelMode
  value: TravelMode
  icon: string
  enabled: boolean
}

export enum TravelMode {
  DRIVING = 'driving',
  WALKING = 'walking',
  BICYCLING = 'bicycling',
  TRANSIT = 'transit',
  PASSENGER = 'passenger'
}

export enum GoogleAPiStatus {
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  QUEUED = 'QUEUED'
}

export enum InternalDistanceError {
  SAME_ORIGIN_DESTINATION = 'SAME_ORIGIN_DESTINATION',
  CONFIGURATION_ERROR = 'CONFIGURATION_ERROR',
  UNKNOWN = 'UNKNOWN'
}

export enum GoogleDistanceApiStatus {
  OK = 'OK',
  INVALID_REQUEST = 'INVALID_REQUEST',
  MAX_ELEMENTS_EXCEEDED = 'MAX_ELEMENTS_EXCEEDED',
  MAX_DIMENSIONS_EXCEEDED = 'MAX_DIMENSIONS_EXCEEDED',
  OVER_DAILY_LIMIT = 'OVER_DAILY_LIMIT',
  OVER_QUERY_LIMIT = 'OVER_QUERY_LIMIT',
  REQUEST_DENIED = 'REQUEST_DENIED',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  ZERO_RESULTS = 'ZERO_RESULTS',
  NOT_FOUND = 'NOT_FOUND'
}

export enum GoogleDistanceElementStatus {
  OK = 'OK',
  ZERO_RESULTS = 'ZERO_RESULTS',
  NOT_FOUND = 'NOT_FOUND',
  MAX_ROUTE_LENGTH_EXCEEDED = 'MAX_ROUTE_LENGTH_EXCEEDED'
}
